import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MoreVertical, Power, Archive, ArchiveRestore, Trash2, X } from "lucide-react";

/**
 * Reusable kebab-menu for meal plan / special cards.
 * Wires up: Active toggle, Archive/Unarchive, Delete.
 *
 * Props:
 *   plan        — { id, active, archived }
 *   resource    — "meal-plans" or "specials"
 *   accentClass — bg color class for the kebab button (e.g. "bg-slate-200 text-slate-700")
 *   onChanged   — () => void
 *   testid      — optional data-testid prefix
 */
export default function PlanCardActions({ plan, resource = "meal-plans", accentClass = "bg-slate-200 text-slate-700", onChanged, testid = "plan" }) {
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const active = plan.active !== false;
  const archived = !!plan.archived;

  const call = async (path, method = "patch", body = null) => {
    try {
      const url = `/${resource}/${plan.id}${path}`;
      if (method === "patch") await api.patch(url, body || {});
      else if (method === "delete") await api.delete(url);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <div className="relative" onClick={stop}>
      <button data-testid={`${testid}-actions-${plan.id}`}
        onClick={(e) => { stop(e); setOpen(o => !o); }}
        className={`p-1.5 rounded-full ${accentClass} hover:opacity-80`} title="More actions">
        <MoreVertical size={11} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={(e) => { stop(e); setOpen(false); }} />
          <div className="absolute right-0 top-full mt-1 z-40 bg-white border border-border rounded-xl shadow-floating min-w-[200px] py-1 text-left">
            <button data-testid={`${testid}-toggle-active-${plan.id}`}
              onClick={async (e) => { stop(e); await call("/active", "patch", { active: !active }); toast.success(active ? "Set inactive" : "Set active"); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-ink-700 hover:bg-cream-100 flex items-center gap-2">
              <Power size={12} className={active ? "text-emerald-600" : "text-ink-500"} />
              {active ? "Set inactive" : "Set active"}
            </button>
            {!archived ? (
              <button data-testid={`${testid}-archive-${plan.id}`}
                onClick={async (e) => { stop(e); await call("/archive"); toast.success("Archived"); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-ink-700 hover:bg-cream-100 flex items-center gap-2">
                <Archive size={12} /> Archive
              </button>
            ) : (
              <button data-testid={`${testid}-unarchive-${plan.id}`}
                onClick={async (e) => { stop(e); await call("/unarchive"); toast.success("Unarchived"); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-ink-700 hover:bg-cream-100 flex items-center gap-2">
                <ArchiveRestore size={12} /> Unarchive
              </button>
            )}
            <button data-testid={`${testid}-delete-${plan.id}`}
              onClick={(e) => { stop(e); setConfirmDel(true); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50 flex items-center gap-2">
              <Trash2 size={12} /> Delete permanently
            </button>
          </div>
        </>
      )}
      {confirmDel && (
        <div className="fixed inset-0 bg-ink/40 z-[60] flex items-center justify-center p-4" onClick={(e) => { stop(e); setConfirmDel(false); }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={stop}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-extrabold text-rose-700">Delete this plan?</h3>
              <button onClick={(e) => { stop(e); setConfirmDel(false); }} className="p-1 rounded-full hover:bg-cream-200"><X size={14} /></button>
            </div>
            <p className="text-sm text-ink-700 mb-4">
              This permanently removes <strong>{plan.name}</strong>. Already-recorded consumption stays intact, but the plan itself cannot be reused. Use <em>Archive</em> instead to hide it but keep it editable.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={(e) => { stop(e); setConfirmDel(false); }} className="px-4 py-2 rounded-full text-sm font-semibold bg-cream-200 text-ink-700 hover:bg-cream-300">Cancel</button>
              <button data-testid={`${testid}-confirm-delete-${plan.id}`}
                onClick={async (e) => { stop(e); await call("", "delete"); toast.success("Deleted"); setConfirmDel(false); }}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-rose-700 text-white hover:bg-rose-800">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
